import { default as FireOrangeIcon } from "@/assets/icons/fire_orange.svg"
import { appGrowthHackingSupplyWithdrawPopupState } from "@/atoms/appGrowthHackingState"
import { appModalDisableState } from "@/atoms/appModalDisableState"
import { appBorrowRepayPopupState, appSupplyWithdrawPopupState } from "@/atoms/appPopupState"
import { selectedReserveAtom } from "@/atoms/modal"
import { selectedPoolAtom } from "@/atoms/shared"
import { ButtonConnect } from "@/components/buttonConnect"
import useTrans from "@/hooks/useTrans"
import useWalletConnect from "@/hooks/useWalletConnect"
import {
  AMPLITUDE_EVENT_MAP,
  BORROW,
  DEPOSIT,
  IS_HAPPY_PI_DAY,
  IS_MAINNET,
  PI_RESERVE_ITEM,
  REPAY,
  SUPPLY,
  WITHDRAW,
} from "@/utils/constants"
import { sendEventLog } from "@/utils/tracking-events"
import { Box, Stack, Tab, Tabs } from "@mui/material"
import { ReserveType } from "@renec-foundation/relend-adapter"
import { useAtom, useSetAtom } from "jotai"
import Image from "next/image"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSessionStorage } from "usehooks-ts"
import BorrowView from "./components/BorrowView"
import SupplyView from "./components/SupplyView"
const View = () => {
  const trans = useTrans()
  const [hasMounted, setHasMounted] = useState(false)
  const [tab, setTab] = useSessionStorage<typeof DEPOSIT | typeof BORROW>("supplyBorrowTab", DEPOSIT)

  // atoms
  const setGrowthHackingSupplyWithdrawPopupState = useSetAtom(appGrowthHackingSupplyWithdrawPopupState)
  const setSupplyWithdrawPopupState = useSetAtom(appSupplyWithdrawPopupState)
  const setBorrowRepayPopupState = useSetAtom(appBorrowRepayPopupState)
  const setModalDisableState = useSetAtom(appModalDisableState)
  const setSelectedReserve = useSetAtom(selectedReserveAtom)
  const [selectedPool] = useAtom(selectedPoolAtom)

  const walletConnectButtonRef = useRef<HTMLButtonElement | null>(null)
  const { connected, isSolanaChain } = useWalletConnect()

  const handleConnectWalletClick = useCallback(() => {
    if (walletConnectButtonRef.current) {
      const demonWalletConnectButton = walletConnectButtonRef.current.querySelector("button")
      if (demonWalletConnectButton) {
        demonWalletConnectButton.click()
      }
    }
  }, [])

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newTab: typeof DEPOSIT | typeof BORROW) => {
      setTab(newTab)
      const eventName = newTab === DEPOSIT ? "supply" : "borrow"
      sendEventLog(`view_supply_borrow_${eventName}_tab`, {
        isMainnet: IS_MAINNET,
      })
    },
    [setTab],
  )

  const disableModal = useCallback(() => {
    setModalDisableState(true)

    setTimeout(() => {
      setModalDisableState(false)
    }, 1000)
  }, [setModalDisableState])

  const handleOpenGrowthHackingPopup = useCallback(
    (item: ReserveType, type: typeof SUPPLY | typeof BORROW) => {
      const tokenName = PI_RESERVE_ITEM.name

      if (connected) {
        setGrowthHackingSupplyWithdrawPopupState({
          isOpen: true,
          type,
          tokenName,
        })
        setSelectedReserve(item.address)
        disableModal()
      } else {
        handleConnectWalletClick()
      }
      sendEventLog(type === SUPPLY ? AMPLITUDE_EVENT_MAP.deposit : AMPLITUDE_EVENT_MAP.borrow, {
        isMainnet: IS_MAINNET,
        token_name: tokenName,
      })
    },
    [connected, disableModal, setGrowthHackingSupplyWithdrawPopupState, setSelectedReserve, handleConnectWalletClick],
  )

  const handleOpenSupplyWithdrawPopup = useCallback(
    (item: ReserveType, type: typeof DEPOSIT | typeof WITHDRAW) => {
      if ([PI_RESERVE_ITEM.address].includes(item.address)) {
        handleOpenGrowthHackingPopup(item, SUPPLY)
        return
      }
      if (connected) {
        setSupplyWithdrawPopupState({ isOpen: true, type })
        setSelectedReserve(item.address)
        disableModal()
      } else {
        handleConnectWalletClick()
      }

      // amplitude events
      sendEventLog("view_supply_popup", {
        isMainnet: IS_MAINNET,
      })
    },
    [
      connected,
      disableModal,
      handleOpenGrowthHackingPopup,
      handleConnectWalletClick,
      setSelectedReserve,
      setSupplyWithdrawPopupState,
    ],
  )

  const handleOpenBorrowRepayPopup = useCallback(
    (item: ReserveType, type: typeof BORROW | typeof REPAY) => {
      if ([PI_RESERVE_ITEM.address].includes(item.address)) {
        handleOpenGrowthHackingPopup(item, BORROW)
        return
      }
      if (connected) {
        setBorrowRepayPopupState({ isOpen: true, type })
        setSelectedReserve(item.address)
        disableModal()
      } else {
        handleConnectWalletClick()
      }

      // amplitude events
      sendEventLog("view_borrow_popup", {
        isMainnet: IS_MAINNET,
      })
    },
    [
      connected,
      disableModal,
      setSelectedReserve,
      setBorrowRepayPopupState,
      handleConnectWalletClick,
      handleOpenGrowthHackingPopup,
    ],
  )

  const reserves = useMemo(() => {
    if (!selectedPool) return []

    let data = selectedPool.reserves ?? []
    if (data.length > 0 && IS_HAPPY_PI_DAY) {
      data = data.concat([PI_RESERVE_ITEM])
    }

    return data
  }, [selectedPool])

  // This is for the tabbing. This component will be rendered on client-side
  // Will be added for skeleton layout in the future.
  useEffect(() => {
    setHasMounted(true)
  }, [])

  if (!hasMounted) {
    return null
  }

  return (
    <Stack gap={3} data-testid='overview-supply-borrow-view'>
      <Tabs
        value={tab}
        onChange={handleTabChange}
        TabIndicatorProps={{ sx: { display: "none" } }}
        aria-label='overview-tabs-sypply-borrow'
        variant='fullWidth'
        className='bg-white border border-solid border-secondary rounded-lg p-[2px]'
        id='overview-supply-borrow-tabs'
      >
        <Tab
          classes={{ selected: "bg-secondary !text-primary font-semibold rounded-lg" }}
          className='flex-1 py-2 text-sm sm:text-lg font-medium text-neutral-500'
          value={DEPOSIT}
          label={trans.dashboard.table.supply}
          data-testid='overview-supply-tab'
        />
        <Tab
          classes={{ selected: "bg-secondary !text-primary font-semibold rounded-lg" }}
          className='flex-1 py-2 text-sm sm:text-lg font-medium text-neutral-500'
          value={BORROW}
          label={
            <Box className='flex justify-center items-center gap-1'>
              {trans.dashboard.table.borrow}
              {isSolanaChain ? <Image src={FireOrangeIcon} alt='highlight-icon' width={24} height={24} /> : null}
            </Box>
          }
          data-testid='overview-borrow-tab'
        />
      </Tabs>
      {tab === DEPOSIT ? (
        <SupplyView reserves={reserves} handleOpenSupplyWithdrawPopup={handleOpenSupplyWithdrawPopup} />
      ) : (
        <BorrowView reserves={reserves} handleOpenBorrowRepayPopup={handleOpenBorrowRepayPopup} />
      )}

      <Box display='none' ref={walletConnectButtonRef}>
        <ButtonConnect />
      </Box>
    </Stack>
  )
}

export default View
